import { useEffect, useRef, useState } from 'react';

interface UseServerStatsIntervalProps {
    fetchFunction: () => Promise<void>;
    interval: number;
    enabled: boolean;
    maxRetries?: number;
    retryDelay?: number;
}

export const useServerStatsInterval = ({
    fetchFunction,
    interval,
    enabled,
    maxRetries = 3,
    retryDelay = 1000
}: UseServerStatsIntervalProps) => {
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isActive, setIsActive] = useState(false);

    const clearAllTimers = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    };

    const executeRequest = async () => {
        try {
            await fetchFunction();
            setRetryCount(0);
        } catch (error) {
            console.error('Ошибка при получении статистики сервера:', error);

            if (retryCount < maxRetries) {
                setRetryCount(prev => prev + 1);
                console.log(`Повторная попытка ${retryCount + 1}/${maxRetries} через ${retryDelay}ms`);

                retryTimeoutRef.current = setTimeout(() => {
                    executeRequest();
                }, retryDelay * (retryCount + 1));
            } else {
                console.error('Достигнуто максимальное количество попыток. Останавливаем интервальные запросы.');
                clearAllTimers();
                setIsActive(false);
                setRetryCount(0);
            }
        }
    };

    const startInterval = () => {
        if (!enabled || isActive) return;

        clearAllTimers();
        setRetryCount(0);
        setIsActive(true);

        executeRequest();

        intervalRef.current = setInterval(() => {
            executeRequest();
        }, interval);
    };

    const stopInterval = () => {
        clearAllTimers();
        setIsActive(false);
        setRetryCount(0);
    };

    useEffect(() => {
        if (enabled) {
            startInterval();
        } else {
            stopInterval();
        }

        return () => {
            clearAllTimers();
        };
    }, [enabled, interval]);

    useEffect(() => {
        return () => {
            clearAllTimers();
        };
    }, []);

    return {
        isActive,
        retryCount,
        startInterval,
        stopInterval
    };
}; 